/**
 * Jito tip account support for MEV protection.
 *
 * Relocated from frontend/src/lib/tx-engine/jito.ts as part of the auth
 * boundary migration (spec 009 FILE 14 Group E). Imports only
 * @solana/web3.js (universal) — platform-neutral, safe in src/auth/ core.
 */

import { PublicKey, SystemProgram, type TransactionInstruction } from '@solana/web3.js';

import type { WalletType } from '../types.js';

/** 8 known Jito tip account pubkeys. */
export const JITO_TIP_ACCOUNTS: string[] = [
  '96gYZGLnJYVFmbjzopPSU6QiEV5fGqZNyN9nmNhvrZU5',
  'HFqU5x63VTqvQss8hp11i4bPu53eAGo6a7B4yDBnvips',
  'Cw8CFyM9FkoMi7K7Crf6HNQqf4uEMzpKw6QNghXLvLkY',
  'ADaUMid9yfUytqMBgopwjb2DTLSLBVCyDELBi51pcWnD',
  'DfXygSm4jCyNCybVYYK6DwvWqjKee8pbDmJGcLWNDXjh',
  'ADuUkR4vqLUMWXxW9gh6D6L8pMSawimctcNZ5pGwDcEt',
  'DttWaMuVvTiduZRnguLF7jNxTgiMBZ1hyAumKUiL2KRL',
  '3AVi9Tg9Uo68tJfuvoKvqKNWKkC5wPdSSdeBnizKZ6jT',
];

/** Select a random tip account pubkey. */
export function selectRandomTipAccount(): PublicKey {
  const index = Math.floor(Math.random() * JITO_TIP_ACCOUNTS.length);
  const account = JITO_TIP_ACCOUNTS[index] ?? JITO_TIP_ACCOUNTS[0];
  // Both accesses are safe: array has 8 constant entries. Fallback for noUncheckedIndexedAccess.
  /* v8 ignore start -- `account` is always defined (8 constant entries); fallback is a noUncheckedIndexedAccess guard */
  return new PublicKey(account ?? '96gYZGLnJYVFmbjzopPSU6QiEV5fGqZNyN9nmNhvrZU5');
  /* v8 ignore stop */
}

/** Create a SOL transfer instruction to a random Jito tip account. */
export function createJitoTipInstruction(
  fromPubkey: PublicKey,
  tipLamports: number,
): TransactionInstruction {
  const tipAccount = selectRandomTipAccount();
  return SystemProgram.transfer({
    fromPubkey,
    toPubkey: tipAccount,
    lamports: tipLamports,
  });
}

/** Check if Jito tipping is available (mainnet + external wallet only). */
export function isJitoAvailable(network: string, walletType: WalletType): boolean {
  return network === 'mainnet' && walletType === 'external';
}

/** Check if a serialized transaction is within the 1232 byte limit. */
export function checkTransactionSize(serializedTx: Uint8Array): boolean {
  return serializedTx.length <= 1232;
}
